<!-- src/components/common/AiHelpAssistant.vue -->
<template>
  <div class="fixed bottom-0 right-2 z-[999] no-print text-left">
    
    <!-- 1. FLOATING ACTION BUTTON (CUSTOM ROBOT DROID) -->
    <button 
      @click="toggleChat" 
      class="relative group cursor-pointer select-none h-28 w-28 rounded-full bg-transparent transition-all outline-none border-none focus:outline-none focus:ring-0 flex items-center justify-center hover:scale-105 active:scale-95"
      title="Ask SIM"
    >
      <img src="/images/sim.png" alt="SIM" class="h-[95%] w-[95%] object-contain" />
      
      <!-- Subtle Dynamic Ping Wave -->
      <span class="absolute inset-0 rounded-full border-4 border-[#20FCF1]/40 animate-ping opacity-0 pointer-events-none"></span>
    </button>

    <!-- 2. AI PORTAL CHAT PANEL -->
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform scale-95 translate-y-8 opacity-0"
      enter-to-class="transform scale-100 translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform scale-100 translate-y-0 opacity-100"
      leave-to-class="transform scale-95 translate-y-8 opacity-0"
    >
      <div 
        v-if="isOpen" 
        class="absolute bottom-24 right-0 bg-white rounded-[32px] border border-slate-200 shadow-2xl overflow-hidden w-80 sm:w-96 h-[540px] flex flex-col border-b-4 border-b-[#20FCF1]"
      >
        <!-- Chat Header (Midnight Premium Styling) -->
        <div class="bg-[#0A1630] px-6 py-4 flex items-center justify-between border-b-4 border-[#20FCF1] shrink-0">
          <div class="flex items-center gap-3">
             <img src="/images/sim-head.png" alt="SIM" class="h-10 w-10 object-contain rounded-full bg-white p-1 border border-[#20FCF1]/30 shadow-sm" />
             <div class="text-left">
                <h4 class="text-sm font-black text-white uppercase tracking-tight leading-none">SIM</h4>
                <span class="text-[8px] font-black text-[#20FCF1] uppercase tracking-[0.2em] block mt-1">Sentinel Intelligence Module</span>
             </div>
          </div>
          <button @click="closeChat" class="text-slate-400 hover:text-rose-500 transition-colors p-1 rounded-lg">
             <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- Chat Conversation Registry -->
        <div 
          ref="messageContainer" 
          class="flex-grow p-6 overflow-y-auto custom-scrollbar bg-slate-50 space-y-4 text-left"
        >
          <div 
            v-for="(msg, idx) in messages" 
            :key="idx" 
            :class="msg.sender === 'user' ? 'justify-end' : 'justify-start'"
            class="flex items-start gap-2.5 animate-fadeIn"
          >
            <!-- SIM Avatar (Bot Message Only) -->
            <img 
              v-if="msg.sender === 'bot'" 
              src="/images/sim-head.png" 
              class="h-7 w-7 object-contain rounded-full bg-white p-0.5 border border-slate-100 shadow-sm" 
              alt="Bot"
            />
            
            <div 
              :class="msg.sender === 'user' ? 'bg-[#0A1630] text-white rounded-tr-none' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-none'"
              class="max-w-[80%] px-4 py-3 rounded-2xl text-xs font-semibold leading-relaxed shadow-sm break-words text-left"
            >
              {{ msg.text }}
            </div>
          </div>

          <!-- Typing Indicator -->
          <div v-if="isTyping" class="flex items-start gap-2.5 justify-start animate-fadeIn">
            <img src="/images/sim-head.png" class="h-7 w-7 object-contain rounded-full bg-white p-0.5 border border-slate-100 shadow-sm" alt="Bot" />
            <div class="bg-white border border-slate-200 px-4 py-3.5 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-1">
              <span class="typing-dot"></span>
              <span class="typing-dot" style="animation-delay: 0.2s;"></span>
              <span class="typing-dot" style="animation-delay: 0.4s;"></span>
            </div>
          </div>
        </div>

        <!-- Dynamic Handshake Prompt Input Area -->
        <form 
          @submit.prevent="handleSend" 
          class="p-4 bg-white border-t border-slate-100 flex items-center gap-2.5 shrink-0"
        >
          <input 
            v-model="userInput" 
            type="text" 
            placeholder="Ask SIM a question..." 
            class="flex-grow bg-slate-50 border border-slate-200 rounded-2xl px-5 py-3.5 text-xs font-bold text-[#0A1630] focus:outline-none focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all shadow-inner placeholder-slate-400"
            :disabled="isTyping"
          />
          <button 
            type="submit" 
            :disabled="!userInput.trim() || isTyping"
            class="bg-[#20FCF1] text-[#0A1630] p-3.5 rounded-2xl shadow-md hover:scale-105 active:scale-95 transition-all disabled:opacity-50 disabled:scale-100 flex items-center justify-center border-none"
          >
            <PaperAirplaneIcon class="h-4 w-4" />
          </button>
        </form>

      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { firebaseApp } from '@/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useUserStore } from '@/stores/userStore';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const functions = getFunctions(firebaseApp, 'europe-west2');
const queryAi = httpsCallable(functions, 'queryAiAssistant');

const isOpen = ref(false);
const isTyping = ref(false);
const userInput = ref('');
const messageContainer = ref(null);

const userName = computed(() => {
  const name = userStore.userProfile?.firstName;
  return name ? name.trim() : 'there';
});

const messages = ref([
  { 
    sender: 'bot', 
    text: `Hello! I am SIM, your Sentinel Intelligence Module. How can I help you today, ${userName.value}?`, 
    timestamp: Date.now() 
  }
]);

// Keep welcome message updated when user profile finishes hydrating
watch(() => userName.value, (newVal) => {
  if (messages.value.length === 1 && messages.value[0].sender === 'bot') {
    messages.value[0].text = `Hello! I am SIM, your Sentinel Intelligence Module. How can I help you today, ${newVal}?`;
  }
});

const scrollToBottom = () => {
  setTimeout(() => {
    if (messageContainer.value) {
      messageContainer.value.scrollTop = messageContainer.value.scrollHeight;
    }
  }, 50);
};

const toggleChat = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    scrollToBottom();
  }
};

const closeChat = () => {
  isOpen.value = false;
};

const handleSend = async () => {
  if (!userInput.value.trim() || isTyping.value) return;
  const text = userInput.value.trim();
  userInput.value = '';

  messages.value.push({ sender: 'user', text, timestamp: Date.now() });
  scrollToBottom();

  isTyping.value = true;
  try {
    const res = await queryAi({ message: text });
    if (res.data && res.data.reply) {
      messages.value.push({ sender: 'bot', text: res.data.reply, timestamp: Date.now() });
    } else {
      messages.value.push({ 
        sender: 'bot', 
        text: "I ran into a temporary system handshake failure. Please try again in a moment.", 
        timestamp: Date.now() 
      });
    }
  } catch (err) {
    console.error(err);
    messages.value.push({ 
      sender: 'bot', 
      text: `I apologise, ${userName.value}. I couldn't reach the system. Please ensure your network connection is active.`, 
      timestamp: Date.now() 
    });
  } finally {
    isTyping.value = false;
    scrollToBottom();
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #20FCF1;
  border-radius: 10px;
}
.typing-dot {
  width: 6px;
  height: 6px;
  background-color: #0A1630;
  border-radius: 50%;
  display: inline-block;
  animation: typing-bounce 1.4s infinite both;
}
@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1.0); }
}
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out forwards;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>